import { Component, OnInit, HostListener, ChangeDetectorRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { KeycloakService } from 'keycloak-angular';
import { ChallengeApiService } from '@angular-workspace/services';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { FooterComponent } from '../../../shared/components/footer/footer.component';
import { AssetHeaderComponent, AssetHeaderConfig } from '../../../shared/components/asset-header/asset-header.component';
import { ManageChallengesSidebarComponent } from '../manage-challenges-sidebar/manage-challenges-sidebar.component';
import { ViewMode } from '../../../shared/components/asset-grid/asset-grid.component';
import { FeatureCardData } from '../../../shared/components/feature-card/feature-card.component';
import { ChallengeCardComponent, ChallengeCardData } from '../challenge-card/challenge-card.component';
import { CarouselComponent } from '../../../shared/components/primeng/carousel/carousel.component';
import { LoginModalComponent } from '../../../shared/components/login-modal/login-modal.component';
import { ToasterService } from '../../../services/toaster.service';
import { GoogleAnalyticsService } from '../../../services/google-analytics.service';

@Component({
  selector: 'app-challenges-home',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    HeaderComponent,
    FooterComponent,
    AssetHeaderComponent,
    ManageChallengesSidebarComponent,
    ChallengeCardComponent,
    CarouselComponent,
    LoginModalComponent
  ],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
})
export class ChallengesHomeComponent implements OnInit {
  activeTab: string = 'overview';
  activeLabel: string = '';
  
  // Challenge card data (Overview carousels)
  newChallenges: ChallengeCardData[] = [];
  underEvaluation: ChallengeCardData[] = [];
  pastChallenges: ChallengeCardData[] = [];

  /** New Challenges tab: list, pagination, loading, search, view mode, show. */
  newChallengesTabData: ChallengeCardData[] = [];
  newChallengesTabLoading: boolean = false;
  newChallengesTabCurrentPage: number = 1;
  newChallengesTabTotalPages: number = 1;
  newChallengesTabItemsPerPage: number = 6;
  newChallengesTabSearchQuery: string = '';
  newChallengesTabViewMode: ViewMode = 'grid';
  newChallengesTabShowDropdownOpen: boolean = false;
  newChallengesTabItemsPerPageOptions: number[] = [6, 12, 24, 48];
  /** Sort options: New to old, Old to new, Highest Votes. */
  newChallengesTabSortOption: string = 'new-to-old';
  newChallengesTabSortDropdownOpen: boolean = false;
  newChallengesTabSortOptions: { value: string; label: string }[] = [
    { value: 'new-to-old', label: 'New to old' },
    { value: 'old-to-new', label: 'Old to new' },
    { value: 'highest-votes', label: 'Highest Votes' }
  ];
  newChallengesTabBookmarksActive: boolean = false;
  newChallengesTabLikeActive: boolean = false;
  /** From Keycloak; when false, Bookmarks sidebar click shows login modal. */
  isLoggedIn: boolean = false;
  /** Login modal when user clicks Bookmarks or Challenges Submitted in sidebar but is not logged in. */
  showLoginModal: boolean = false;

  /** Loading state for Bookmarks tab (list from listBookmark API). */
  bookmarksLoading: boolean = false;
  /** Bookmarks tab: same card design as Overview, uses ChallengeCardData[]. */
  bookmarkedChallengesCardData: ChallengeCardData[] = [];
  bookmarksTabShowDropdownOpen: boolean = false;
  bookmarksTabItemsPerPageOptions: number[] = [6, 12, 24, 48];
  /** Loading state for Under Evaluation tab (list from evaluationChallenge public API). */
  underEvaluationLoading: boolean = false;
  /** Loading state for Past Challenges tab (list from pastChallenge public API). */
  pastChallengesLoading: boolean = false;
  /** Sort option for Under Evaluation tab (same options as New Challenges). */
  underEvaluationSortOption: string = 'new-to-old';
  underEvaluationSortDropdownOpen: boolean = false;
  /** Sort option for Past Challenges tab (same options as New Challenges). */
  pastChallengesSortOption: string = 'new-to-old';
  pastChallengesSortDropdownOpen: boolean = false;
  /** Challenge card data for Past Challenges / Under Evaluation tabs (same design as New Challenges). */
  pastChallengesCardData: ChallengeCardData[] = [];
  underEvaluationCardData: ChallengeCardData[] = [];
  pastUnderEvalShowDropdownOpen: boolean = false;
  pastUnderEvalItemsPerPageOptions: number[] = [6, 12, 24, 48];

  /** Challenges Submitted: active sub-tab */
  submittedSubTab: 'interested' | 'submitted' | 'in-evaluation' | 'completed' = 'interested';
  submittedLoading: boolean = false;
  /** Which row's action dropdown is open (row id or challengeId). */
  submittedActionDropdownOpenId: string | null = null;
  /** Interested table (from getParticipatedChallenges). */
  interestedRows: { challengeId: string; challengeName: string; totalPrize: string; submissionStartDate: string; submissionEndDate: string }[] = [];
  /** Submitted table (from getSubmittedSubmissions). */
  submittedRows: { challengeId: string; submissionId: string; challengeName: string; solutionName: string; submissionStartDate: string }[] = [];
  /** In Evaluation table (from getEvaluationSubmissions). */
  inEvaluationRows: { challengeId: string; submissionId: string; challengeName: string; solutionName: string; evaluationEndDate: string }[] = [];
  /** Completed table (from getCompletedSubmissions). */
  completedRows: { challengeId: string; submissionId: string; challengeName: string; solutionName: string; evaluationEndDate: string; results: string; comments: string }[] = [];

  // Grid data
  challenges: FeatureCardData[] = [];
  allChallenges: FeatureCardData[] = [];
  
  // Pagination and view
  currentPage: number = 1;
  totalPages: number = 1;
  viewMode: ViewMode = 'grid';
  itemsPerPage: number = 6;

  // Carousel: responsive items per slide (desktop: 3, tablet: 2, mobile: 1)
  carouselItemsToShow: number = 3;

  /** Default challenge image for list view when imageUrl is missing */
  defaultChallengeImageUrl = 'https://images.unsplash.com/photo-1464226184884-fa280b87c399?w=400&h=200&fit=crop';

  /** List view: natural width per challenge id, used for object-fit (width > 1000 → contain). */
  listViewImageWidths: Record<string, number> = {};
  private cdr = inject(ChangeDetectorRef);
  private newChallengesSearchDebounce: ReturnType<typeof setTimeout> | null = null;
  private readonly NEW_CHALLENGES_SEARCH_DEBOUNCE_MS = 400;
  private readonly NEW_CHALLENGES_SEARCH_MIN_LENGTH = 3;

  get headerConfig(): AssetHeaderConfig {
    return {
      breadcrumbs: [
        { label: 'Home', route: '/' },
        { label: 'Challenges' }
      ],
      title: 'Challenges',
      description: 'Participate in exciting challenges and competitions to showcase your data science and machine learning skills.'
    };
  }

  private keycloak = inject(KeycloakService);
  private toasterService = inject(ToasterService);
  private googleAnalytics = inject(GoogleAnalyticsService);

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private challengeApiService: ChallengeApiService
  ) {}

  async ngOnInit(): Promise<void> {
    this.updateCarouselItemsToShow();
    this.loadOverviewData();
    this.loadChallenges();
    try {
      this.isLoggedIn = await this.keycloak.isLoggedIn();
      this.cdr.markForCheck();
    } catch {
      this.isLoggedIn = false;
    }

    // Subscribe to query params for tab navigation
    this.route.queryParams.subscribe(async params => {
      const tab = params['tab'] || 'overview';
      // Bookmarks and Challenges Submitted require login: show login modal and stay on current view
      if (tab === 'bookmarked' || tab === 'your-competitions') {
        const loggedIn = await this.keycloak.isLoggedIn();
        this.isLoggedIn = loggedIn;
        this.cdr.markForCheck();
        if (!loggedIn) {
          this.showLoginModal = true;
          this.router.navigate(['/challenges'], { queryParams: {} });
          return;
        }
      }
      this.setActiveTab(tab);
    });
  }

  @HostListener('document:click')
  closeNewChallengesTabDropdowns(): void {
    this.newChallengesTabShowDropdownOpen = false;
    this.newChallengesTabSortDropdownOpen = false;
    this.bookmarksTabShowDropdownOpen = false;
    this.underEvaluationSortDropdownOpen = false;
    this.pastChallengesSortDropdownOpen = false;
    this.submittedActionDropdownOpenId = null;
  }

  @HostListener('window:resize')
  updateCarouselItemsToShow(): void {
    const width = window.innerWidth;
    if (width < 768) {
      this.carouselItemsToShow = 1;
    } else if (width < 992) {
      this.carouselItemsToShow = 2;
    } else {
      this.carouselItemsToShow = 3;
    }
  }

  setActiveTab(tab: string): void {
    this.activeTab = tab;
    this.updateActiveLabel();
    if (tab === 'new-challenges') {
      this.loadNewChallengesTab();
    } else if (tab === 'bookmarked') {
      this.loadBookmarkedChallenges();
    } else if (tab === 'ongoing-challenges') {
      this.loadUnderEvaluationChallenges();
    } else if (tab === 'past-challenges') {
      this.loadPastChallenges();
    } else if (tab === 'your-competitions') {
      this.loadChallengesSubmittedData();
    } else {
      this.loadChallenges();
    }
  }

  setSubmittedSubTab(subTab: 'interested' | 'submitted' | 'in-evaluation' | 'completed'): void {
    this.submittedSubTab = subTab;
    this.submittedActionDropdownOpenId = null;
    this.loadChallengesSubmittedSubTab(subTab);
  }

  toggleSubmittedActionDropdown(id: string): void {
    this.submittedActionDropdownOpenId = this.submittedActionDropdownOpenId === id ? null : id;
  }

  async onSubmittedSolutionDownload(row: { submissionId: string }): Promise<void> {
    this.submittedActionDropdownOpenId = null;
    const submissionId = row?.submissionId?.trim();
    if (!submissionId) {
      this.toasterService.error('Solution download is not available for this submission.');
      return;
    }
    try {
      const res = await this.challengeApiService.getChallengeSolution(submissionId);
      const urls = res?.data?.download_urls;
      if (urls && Array.isArray(urls) && urls.length > 0) {
        for (const url of urls) {
          if (url) window.open(url, '_blank', 'noopener,noreferrer');
        }
      } else {
        this.toasterService.error('Download URLs could not be retrieved.');
      }
    } catch (err) {
      this.toasterService.error('Failed to get solution download.');
    }
  }

  onSubmittedResubmission(challengeId: string): void {
    this.submittedActionDropdownOpenId = null;
    this.router.navigate(['/challenges', challengeId], { queryParams: { resubmit: '1' } });
  }

  /** Load Challenges Submitted data: on page load load Interested tab. */
  loadChallengesSubmittedData(): void {
    this.loadChallengesSubmittedSubTab('interested');
  }

  /** Load data for one Challenges Submitted sub-tab. */
  async loadChallengesSubmittedSubTab(subTab: 'interested' | 'submitted' | 'in-evaluation' | 'completed'): Promise<void> {
    this.submittedLoading = true;
    this.cdr.markForCheck();
    const payload = { page: 1, limit: 10 };
    try {
      if (subTab === 'interested') {
        const response = await this.challengeApiService.getParticipatedChallenges({
          ...payload,
          sort_by: 'submission_starts_at',
          sort_order: 'desc'
        });
        const list = this.getChallengeListFromResponse(response) ?? response?.data?.participated ?? response?.data ?? [];
        const arr = Array.isArray(list) ? list : [];
        this.interestedRows = arr.map((item: any) => {
          const c = item?.competition ?? item;
          const id = c?.id ?? c?._id ?? item?.id ?? '';
          const name = c?.title ?? c?.name ?? item?.title ?? '';
          const prize = c?.prize_pools?.total_pool_amount ?? c?.prize_money ?? item?.prize ?? 0;
          const currency = c?.prize_pools?.currency ?? c?.currency ?? 'INR';
          const sym = currency?.toUpperCase() === 'USD' ? '$' : '₹';
          const timelines = c?.timelines ?? item?.timelines ?? {};
          const start = timelines?.submission_starts_at ?? c?.submission_starts_at ?? item?.submission_starts_at ?? '';
          const end = timelines?.submission_ends_at ?? c?.submission_ends_at ?? item?.submission_ends_at ?? '';
          return {
            challengeId: id,
            challengeName: name,
            totalPrize: `${sym}${Number(prize).toLocaleString('en-IN')}`,
            submissionStartDate: this.formatSubmittedDate(start),
            submissionEndDate: this.formatSubmittedDate(end)
          };
        });
      } else if (subTab === 'submitted') {
        const response = await this.challengeApiService.getSubmittedSubmissions({
          ...payload,
          sort_by: 'created_at',
          sort_order: 'desc'
        });
        const list = response?.data?.submissions ?? response?.data ?? response?.results ?? [];
        const arr = Array.isArray(list) ? list : [];
        this.submittedRows = arr.map((item: any) => {
          const c = item?.competition ?? item?.challenge ?? item;
          const id = c?.id ?? c?._id ?? item?.challenge_id ?? item?.challengeId ?? '';
          const submissionId = item?.id ?? item?.submission_id ?? item?._id ?? '';
          const name = c?.title ?? c?.name ?? item?.challenge_title ?? '';
          const solutionName = item?.title ?? item?.solution_name ?? item?.name ?? '—';
          const created = item?.created_at ?? item?.submission_start_date ?? '';
          return {
            challengeId: id,
            submissionId: String(submissionId),
            challengeName: name,
            solutionName,
            submissionStartDate: this.formatSubmittedDate(created)
          };
        });
      } else if (subTab === 'in-evaluation') {
        const response = await this.challengeApiService.getEvaluationSubmissions({
          ...payload,
          sort_by: 'evaluation_ends_at',
          sort_order: 'desc'
        });
        const list = response?.data?.submissions ?? response?.data ?? response?.results ?? [];
        const arr = Array.isArray(list) ? list : [];
        this.inEvaluationRows = arr.map((item: any) => {
          const c = item?.competition ?? item?.challenge ?? item;
          const id = c?.id ?? c?._id ?? item?.challenge_id ?? '';
          const submissionId = item?.id ?? item?.submission_id ?? item?._id ?? '';
          const name = c?.title ?? c?.name ?? item?.challenge_title ?? '';
          const solutionName = item?.title ?? item?.solution_name ?? '—';
          const evalEnd = item?.evaluation_ends_at ?? c?.timelines?.evaluation_ends_at ?? item?.evaluation_end_date ?? '';
          return {
            challengeId: id,
            submissionId: String(submissionId),
            challengeName: name,
            solutionName,
            evaluationEndDate: this.formatSubmittedDate(evalEnd)
          };
        });
      } else {
        const response = await this.challengeApiService.getCompletedSubmissions({
          ...payload,
          sort_by: 'created_at',
          sort_order: 'desc'
        });
        const list = response?.data?.submissions ?? response?.data ?? response?.results ?? [];
        const arr = Array.isArray(list) ? list : [];
        this.completedRows = arr.map((item: any) => {
          const c = item?.competition ?? item?.challenge ?? item;
          const id = c?.id ?? c?._id ?? item?.challenge_id ?? '';
          const submissionId = item?.id ?? item?.submission_id ?? item?._id ?? '';
          const name = c?.title ?? c?.name ?? item?.challenge_title ?? '';
          const solutionName = item?.title ?? item?.solution_name ?? '—';
          const evalEnd =
            item?.evaluation_ends_at ??
            c?.timelines?.evaluation_ends_at ??
            item?.evaluation_end_date ??
            '';
          const scoreRaw = item?.score ?? item?.results ?? item?.result;
          const results =
            scoreRaw !== null && scoreRaw !== undefined && scoreRaw !== ''
              ? typeof scoreRaw === 'number'
                ? scoreRaw.toFixed(2)
                : String(scoreRaw)
              : '—';
          const comments = item?.evaluation_comment ?? item?.comments ?? item?.feedback ?? '—';
          return {
            challengeId: id,
            submissionId: String(submissionId),
            challengeName: name,
            solutionName,
            evaluationEndDate: this.formatSubmittedDate(evalEnd),
            results,
            comments: String(comments ?? '—')
          };
        });
      }
    } catch (error) {
      console.error('Error loading challenges submitted tab:', subTab, error);
      if (subTab === 'interested') this.interestedRows = [];
      else if (subTab === 'submitted') this.submittedRows = [];
      else if (subTab === 'in-evaluation') this.inEvaluationRows = [];
      else this.completedRows = [];
    } finally {
      this.submittedLoading = false;
      this.cdr.markForCheck();
    }
  }

  private formatSubmittedDate(value: string | undefined): string {
    if (!value) return '—';
    const d = new Date(value);
    if (isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  updateActiveLabel(): void {
    const labels: { [key: string]: string } = {
      'all': 'All Challenges',
      'new-challenges': 'New Challenges',
      'ongoing-challenges': 'Under Evaluation',
      'past-challenges': 'Past Challenges',
      'your-competitions': 'Challenges Submitted',
      'bookmarked': 'Bookmarks'
    };
    this.activeLabel = labels[this.activeTab] || '';
  }

  navigateToTab(tab: string): void {
    this.router.navigate(['/challenges'], {
      queryParams: { tab: tab === 'overview' ? null : tab }
    });
  }

  loadOverviewData(): void {
    this.fetchNewChallenges();
    this.fetchEvaluationChallenges();
    this.fetchPastChallenges();
  }

  /**
   * Maps API challenge response to ChallengeCardData.
   * API shape: id, title, subtitle, image_url, prize_pools { total_pool_amount, prize_type, prize_description },
   * timelines { submission_ends_at, evaluation_ends_at }, results_announced_at, participant_count, submission_count
   */
  private mapApiChallengeToCardData(item: any): ChallengeCardData {
    const id = item.id ?? item._id ?? '';
    const name = item.title ?? item.name ?? item.challengeName ?? '';
    const description = item.description ?? item.short_description ?? '';
    const provider = item.subtitle ?? item.organization ?? item.provider ?? item.organizer ?? 'NHA Team';
    const imageUrl = item.image_url ?? item.imageUrl ?? item.banner_image ?? undefined;
    const participants = item.participant_count ?? item.interested_participants ?? item.interestedParticipants ?? item.participants ?? 0;
    const submissions = item.submission_count ?? 0;
    const prizePools = item.prize_pools;
    const prizeMoney = prizePools?.total_pool_amount ?? item.prize_money ?? item.prizeMoney ?? item.prize ?? 0;
    const currency = prizePools?.currency ?? item.currency ?? undefined;
    const timelines = item.timelines ?? {};
    const submissionStartsAt = timelines.submission_starts_at ?? item.submission_starts_at ?? undefined;
    const submissionEndsAt = timelines.submission_ends_at ?? item.submission_ends_at ?? item.end_date ?? item.endDate ?? undefined;
    const endDate = item.results_announced_at
      ? null
      : (submissionEndsAt ?? timelines.evaluation_ends_at ?? item.submission_end ?? '');
    const daysRemaining = endDate
      ? Math.max(0, Math.ceil((new Date(endDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
      : 0;
    return {
      id,
      name,
      description,
      provider,
      imageUrl,
      participants,
      submissions,
      prizeMoney,
      currency,
      daysRemaining,
      endDate: endDate || undefined,
      submissionStartsAt: submissionStartsAt || undefined,
      submissionEndsAt: submissionEndsAt || undefined,
      link: `/challenges/${id}`
    };
  }

  /** Extract challenges array from API response (data.competitions or data/results/response as array). */
  private getChallengeListFromResponse(response: any): any[] {
    const list = response?.data?.competitions ?? response?.data ?? response?.results ?? response;
    return Array.isArray(list) ? list : [];
  }

  /** Map API challenge item to FeatureCardData for grid (All/Past/Bookmarks view). Compatible with UsecaseCardData for assetType="usecase". */
  private mapApiChallengeToFeatureCardData(item: any): FeatureCardData {
    const id = item.id ?? item._id ?? '';
    const title = item.title ?? item.name ?? item.challengeName ?? '';
    const organization = item.subtitle ?? item.organization ?? item.provider ?? item.organizer ?? 'NHA Team';
    const description = item.description ?? item.short_description ?? item.subtitle ?? item.timelines?.title ?? '';
    const date = item.created_at ?? item.timelines?.submission_ends_at ?? item.submission_ends_at ?? item.date ?? '';
    return {
      id,
      title,
      shortDescription: description,
      organization,
      tags: item.tags ?? [],
      date: typeof date === 'string' ? date : date ? String(date) : undefined,
      badge: item.badge,
      link: `/challenges/${id}`,
      description: description || '' // UsecaseCardData compatibility (assetType="usecase" grid)
    } as FeatureCardData & { description?: string };
  }

  /** Load Bookmarks tab data via listBookmark() API on tab switch / page change. */
  async loadBookmarkedChallenges(page: number = 1): Promise<void> {
    this.bookmarksLoading = true;
    this.challenges = [];
    this.bookmarkedChallengesCardData = [];
    this.currentPage = page;
    this.cdr.markForCheck();
    try {
      const response = await this.challengeApiService.listBookmark({
        page,
        limit: this.itemsPerPage,
        sort_by: 'Newest'
      });
      // Bookmarked API returns data.bookmarked_competitions (array of { id, created_at, competition })
      const rawList = response?.data?.bookmarked_competitions ?? [];
      const list = Array.isArray(rawList) ? rawList : [];
      const competitionList = list.map((item: any) => item?.competition ?? item).filter((c: any) => c != null);
      this.bookmarkedChallengesCardData = competitionList.map((item: any) => this.mapApiChallengeToCardData(item));
      this.challenges = competitionList.map((item: any) => this.mapApiChallengeToFeatureCardData(item)); // keep for any fallback
      const meta = response?.data?.meta ?? response?.meta;
      this.totalPages = meta?.total_pages ?? Math.max(1, Math.ceil((meta?.total_count ?? this.bookmarkedChallengesCardData.length) / this.itemsPerPage));
    } catch (error) {
      console.error('Error loading bookmarked challenges:', error);
      this.challenges = [];
      this.bookmarkedChallengesCardData = [];
      this.totalPages = 1;
    } finally {
      this.bookmarksLoading = false;
      this.cdr.markForCheck();
    }
  }

  /** Load Under Evaluation tab data via evaluationChallenge() (public, no auth). */
  async loadUnderEvaluationChallenges(page: number = 1): Promise<void> {
    this.underEvaluationLoading = true;
    this.challenges = [];
    this.currentPage = page;
    this.cdr.markForCheck();
    const { sort_by, sort_order } = this.getSortParamsForOption(this.underEvaluationSortOption);
    try {
      const response = await this.challengeApiService.evaluationChallenge({
        page,
        limit: this.itemsPerPage,
        sort_by,
        sort_order
      });
      const list = this.getChallengeListFromResponse(response);
      this.challenges = list.map((item: any) => this.mapApiChallengeToFeatureCardData(item));
      this.underEvaluationCardData = list.map((item: any) => this.mapApiChallengeToCardData(item));
      const meta = response?.data?.meta ?? response?.meta;
      this.totalPages = meta?.total_pages ?? Math.max(1, Math.ceil((meta?.total_count ?? list.length) / this.itemsPerPage));
    } catch (error) {
      console.error('Error loading under evaluation challenges:', error);
      this.challenges = [];
      this.underEvaluationCardData = [];
      this.totalPages = 1;
    } finally {
      this.underEvaluationLoading = false;
      this.cdr.markForCheck();
    }
  }

  /** Load Past Challenges tab data via pastChallenge() (public, no auth). */
  async loadPastChallenges(page: number = 1): Promise<void> {
    this.pastChallengesLoading = true;
    this.challenges = [];
    this.currentPage = page;
    this.cdr.markForCheck();
    const { sort_by, sort_order } = this.getSortParamsForOption(this.pastChallengesSortOption);
    try {
      const response = await this.challengeApiService.pastChallenge({
        page,
        limit: this.itemsPerPage,
        sort_by,
        sort_order
      });
      const list = this.getChallengeListFromResponse(response);
      this.challenges = list.map((item: any) => this.mapApiChallengeToFeatureCardData(item));
      this.pastChallengesCardData = list.map((item: any) => this.mapApiChallengeToCardData(item));
      const meta = response?.data?.meta ?? response?.meta;
      this.totalPages = meta?.total_pages ?? Math.max(1, Math.ceil((meta?.total_count ?? list.length) / this.itemsPerPage));
    } catch (error) {
      console.error('Error loading past challenges:', error);
      this.challenges = [];
      this.pastChallengesCardData = [];
      this.totalPages = 1;
    } finally {
      this.pastChallengesLoading = false;
      this.cdr.markForCheck();
    }
  }

  private async fetchNewChallenges(): Promise<void> {
    try {
      const response = await this.challengeApiService.newChallenges({
        page: 1,
        limit: 6,
        sort_by: 'Newest'
      });
      const list = this.getChallengeListFromResponse(response);
      this.newChallenges = list.map((item: any) => this.mapApiChallengeToCardData(item));
    } catch (error) {
      console.error('Error loading new challenges:', error);
      this.newChallenges = [];
    }
  }

  /** Load New Challenges tab data via newChallenges() API with pagination. Optional query: when length >= 3, sent as &query= to API. */
  async loadNewChallengesTab(page: number = 1, query?: string): Promise<void> {
    this.newChallengesTabLoading = true;
    this.newChallengesTabData = [];
    this.newChallengesTabCurrentPage = page;
    const { sort_by, sort_order } = this.getNewChallengesTabSortParams();
    const queryParam = query != null && query.trim().length >= this.NEW_CHALLENGES_SEARCH_MIN_LENGTH ? query.trim() : undefined;
    try {
      const response = await this.challengeApiService.newChallenges({
        page,
        limit: this.newChallengesTabItemsPerPage,
        sort_by,
        sort_order,
        query: queryParam
      });
      const list = this.getChallengeListFromResponse(response);
      this.newChallengesTabData = list.map((item: any) => this.mapApiChallengeToCardData(item));
      const meta = response?.data?.meta ?? response?.meta;
      this.newChallengesTabTotalPages = meta?.total_pages ?? Math.max(1, Math.ceil((meta?.total_count ?? list.length) / this.newChallengesTabItemsPerPage));
    } catch (error) {
      console.error('Error loading new challenges tab:', error);
      this.newChallengesTabData = [];
      this.newChallengesTabTotalPages = 1;
    } finally {
      this.newChallengesTabLoading = false;
    }
  }

  onNewChallengesTabPageChange(page: number): void {
    if (page < 1 || page > this.newChallengesTabTotalPages) return;
    this.loadNewChallengesTab(page);
  }

  /** Page numbers to show in pagination (dataset collection style, max 5 visible). */
  getNewChallengesTabPageNumbers(): number[] {
    const pages: number[] = [];
    const maxVisible = 5;
    const total = this.newChallengesTabTotalPages;
    let start = Math.max(1, this.newChallengesTabCurrentPage - Math.floor(maxVisible / 2));
    let end = Math.min(total, start + maxVisible - 1);
    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }

  /** Display data: when search has 3+ chars we use API results as-is; otherwise client-side filter. */
  get newChallengesTabFilteredData(): ChallengeCardData[] {
    const q = (this.newChallengesTabSearchQuery || '').trim();
    if (q.length >= this.NEW_CHALLENGES_SEARCH_MIN_LENGTH) {
      return this.newChallengesTabData;
    }
    const qLower = q.toLowerCase();
    if (!qLower) return this.newChallengesTabData;
    return this.newChallengesTabData.filter(
      (c) =>
        (c.name || '').toLowerCase().includes(qLower) ||
        (c.provider || '').toLowerCase().includes(qLower) ||
        (c.description || '').toLowerCase().includes(qLower)
    );
  }

  /** Clear search input and reload list without query. */
  clearNewChallengesSearch(): void {
    this.newChallengesTabSearchQuery = '';
    this.loadNewChallengesTab(1);
  }

  /** Called when user types in New Challenges search; debounced, calls API with &query= after 3+ letters. */
  onNewChallengesTabSearchInput(): void {
    if (this.newChallengesSearchDebounce != null) {
      clearTimeout(this.newChallengesSearchDebounce);
      this.newChallengesSearchDebounce = null;
    }
    this.newChallengesSearchDebounce = setTimeout(() => {
      this.newChallengesSearchDebounce = null;
      const q = (this.newChallengesTabSearchQuery || '').trim();
      if (q.length >= this.NEW_CHALLENGES_SEARCH_MIN_LENGTH) {
        this.loadNewChallengesTab(1, q);
      } else if (q.length === 0) {
        this.loadNewChallengesTab(1);
      }
    }, this.NEW_CHALLENGES_SEARCH_DEBOUNCE_MS);
  }

  onNewChallengesTabViewModeChange(mode: ViewMode): void {
    this.newChallengesTabViewMode = mode;
  }

  onNewChallengesTabItemsPerPageChange(value: number): void {
    this.newChallengesTabItemsPerPage = value;
    this.newChallengesTabShowDropdownOpen = false;
    this.loadNewChallengesTab(1);
  }

  toggleNewChallengesTabShowDropdown(): void {
    this.newChallengesTabShowDropdownOpen = !this.newChallengesTabShowDropdownOpen;
  }

  toggleNewChallengesTabSortDropdown(): void {
    this.newChallengesTabSortDropdownOpen = !this.newChallengesTabSortDropdownOpen;
    if (this.newChallengesTabSortDropdownOpen) this.newChallengesTabShowDropdownOpen = false;
  }

  getNewChallengesTabSelectedSortLabel(): string {
    return this.newChallengesTabSortOptions.find(o => o.value === this.newChallengesTabSortOption)?.label || 'New to old';
  }

  /** Map UI sort option to API sort_by (shared by New Challenges, Under Evaluation, Past Challenges). */
  getSortParamsForOption(option: string): { sort_by: string; sort_order?: string } {
    switch (option) {
      case 'old-to-new': return { sort_by: 'Oldest' };
      case 'highest-votes': return { sort_by: 'Hottest' };
      default: return { sort_by: 'Newest' }; // new-to-old
    }
  }

  /** Map UI sort option to API sort_by (query param: &sort_by=...). */
  private getNewChallengesTabSortParams(): { sort_by: string; sort_order?: string } {
    return this.getSortParamsForOption(this.newChallengesTabSortOption);
  }

  onNewChallengesTabSortOptionSelect(value: string): void {
    this.newChallengesTabSortOption = value;
    this.newChallengesTabSortDropdownOpen = false;
    this.loadNewChallengesTab(1);
  }

  toggleUnderEvaluationSortDropdown(): void {
    this.underEvaluationSortDropdownOpen = !this.underEvaluationSortDropdownOpen;
    this.pastChallengesSortDropdownOpen = false;
  }

  togglePastChallengesSortDropdown(): void {
    this.pastChallengesSortDropdownOpen = !this.pastChallengesSortDropdownOpen;
    this.underEvaluationSortDropdownOpen = false;
  }

  getUnderEvaluationSelectedSortLabel(): string {
    return this.newChallengesTabSortOptions.find(o => o.value === this.underEvaluationSortOption)?.label || 'New to old';
  }

  getPastChallengesSelectedSortLabel(): string {
    return this.newChallengesTabSortOptions.find(o => o.value === this.pastChallengesSortOption)?.label || 'New to old';
  }

  onUnderEvaluationSortOptionSelect(value: string): void {
    this.underEvaluationSortOption = value;
    this.underEvaluationSortDropdownOpen = false;
    this.loadUnderEvaluationChallenges(1);
  }

  onPastChallengesSortOptionSelect(value: string): void {
    this.pastChallengesSortOption = value;
    this.pastChallengesSortDropdownOpen = false;
    this.loadPastChallenges(1);
  }

  /** Handle sort change from asset-grid (Under Evaluation / Past Challenges). */
  onAssetGridSortChange(value: string): void {
    if (this.activeTab === 'ongoing-challenges') {
      this.onUnderEvaluationSortOptionSelect(value);
    } else if (this.activeTab === 'past-challenges') {
      this.onPastChallengesSortOptionSelect(value);
    }
  }

  onNewChallengesTabBookmarksClick(): void {
    if (!this.isLoggedIn) return; // TODO: open login modal if needed
    this.newChallengesTabBookmarksActive = !this.newChallengesTabBookmarksActive;
    // TODO: wire to challenges bookmark API when available
  }

  onNewChallengesTabLikeClick(): void {
    if (!this.isLoggedIn) return;
    this.newChallengesTabLikeActive = !this.newChallengesTabLikeActive;
    // TODO: wire to challenges like API when available
  }

  /** Format prize for table (e.g. ₹1,00,000). */
  formatChallengePrize(c: ChallengeCardData): string {
    const sym = c.currency?.toUpperCase() === 'USD' ? '$' : '₹';
    return `${sym}${(c.prizeMoney ?? 0).toLocaleString('en-IN')}`;
  }

  /** List view: time left label (e.g. "3hr left", "1 day left") matching challenge card. */
  getListTimeLeftLabel(c: ChallengeCardData): string {
    const hours = this.getListTimeLeftHours(c);
    if (hours != null && hours > 0 && hours < 24) {
      return hours === 1 ? '1hr left' : `${hours}hr left`;
    }
    const days = c.daysRemaining ?? 0;
    if (days === 0) return 'Deadline today';
    if (days === 1) return '1 day left';
    return `${days} days left`;
  }

  private getListTimeLeftHours(c: ChallengeCardData): number | null {
    const end = c?.endDate;
    if (!end || typeof end !== 'string') return null;
    const trimmed = end.trim();
    const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(trimmed);
    const endTime = isDateOnly
      ? new Date(trimmed + 'T23:59:59.999').getTime()
      : new Date(end).getTime();
    const now = Date.now();
    if (endTime <= now) return null;
    return Math.ceil((endTime - now) / (1000 * 60 * 60));
  }

  /** Format submission date range for table. */
  formatChallengeSubmissionRange(c: ChallengeCardData): string {
    const start = c.submissionStartsAt;
    const end = c.submissionEndsAt;
    if (!start && !end) return '—';
    const fmt = (d: string) => {
      const x = new Date(d);
      if (isNaN(x.getTime())) return d;
      const day = x.getDate();
      const suf = day === 1 || day === 21 || day === 31 ? 'st' : day === 2 || day === 22 ? 'nd' : day === 3 || day === 23 ? 'rd' : 'th';
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${day}${suf} ${months[x.getMonth()]} ${String(x.getFullYear()).slice(-2)}`;
    };
    if (start && end) return `${fmt(start)} – ${fmt(end)}`;
    return start ? fmt(start) : end ? fmt(end) : '—';
  }

  /** List view: store image natural width on load for object-fit logic. */
  onListImageLoad(c: ChallengeCardData, event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img?.naturalWidth && c?.id) {
      this.listViewImageWidths[c.id] = img.naturalWidth;
      this.cdr.markForCheck();
    }
  }

  /** List view: object-fit contain when image width > 1000px, else cover. */
  getListImageObjectFit(c: ChallengeCardData): 'contain' | 'cover' {
    const w = c?.id ? this.listViewImageWidths[c.id] : undefined;
    return w != null && w > 1000 ? 'contain' : 'cover';
  }

  private async fetchEvaluationChallenges(): Promise<void> {
    try {
      const response = await this.challengeApiService.evaluationChallenge({
        page: 1,
        limit: 6,
        sort_by: 'Newest'
      });
      const list = this.getChallengeListFromResponse(response);
      this.underEvaluation = list.map((item: any) => this.mapApiChallengeToCardData(item));
    } catch (error) {
      console.error('Error loading evaluation challenges:', error);
      this.underEvaluation = [];
    }
  }

  private async fetchPastChallenges(): Promise<void> {
    try {
      const response = await this.challengeApiService.pastChallenge({
        page: 1,
        limit: 6,
        sort_by: 'Newest'
      });
      const list = this.getChallengeListFromResponse(response);
      this.pastChallenges = list.map((item: any) => this.mapApiChallengeToCardData(item));
    } catch (error) {
      console.error('Error loading past challenges:', error);
      this.pastChallenges = [];
    }
  }

  loadChallenges(): void {
    // Sample challenges data
    this.allChallenges = [
      {
        id: '1',
        title: 'Agricultural Data Prediction Challenge',
        organization: 'NHA Team',
        tags: [],
        date: '2024-01-15',
        badge: 'New',
        link: '/challenges/1'
      },
      {
        id: '2',
        title: 'Crop Yield Forecasting Competition',
        organization: 'AgriTech Solutions',
        tags: [],
        date: '2024-01-10',
        badge: 'New',
        link: '/challenges/2'
      },
      {
        id: '3',
        title: 'Weather Pattern Analysis Challenge',
        organization: 'IMD Research Division',
        tags: [],
        date: '2024-01-05',
        badge: 'New',
        link: '/challenges/3'
      },
      {
        id: '4',
        title: 'Soil Quality Assessment Challenge',
        organization: 'Department of Agriculture',
        tags: [],
        date: '2024-01-01',
        badge: 'Under Evaluation',
        link: '/challenges/4'
      },
      {
        id: '5',
        title: 'Irrigation Optimization Competition',
        organization: 'Water Management Board',
        tags: [],
        date: '2023-12-20',
        badge: 'Results Announced',
        link: '/challenges/5'
      },
      {
        id: '6',
        title: 'Pest Detection ML Challenge',
        organization: 'AgriResearch Institute',
        tags: [],
        date: '2023-12-15',
        badge: 'Results Announced',
        link: '/challenges/6'
      }
    ];

    // Filter based on active tab
    let filtered = [...this.allChallenges];
    
    if (this.activeTab === 'new-challenges') {
      filtered = this.allChallenges.filter(c => c.badge === 'New');
    } else if (this.activeTab === 'ongoing-challenges') {
      filtered = this.allChallenges.filter(c => c.badge === 'Under Evaluation');
    } else if (this.activeTab === 'past-challenges') {
      filtered = this.allChallenges.filter(c => c.badge === 'Results Announced');
    } else if (this.activeTab === 'your-competitions') {
      filtered = this.allChallenges.filter(c => c.badge === 'Under Evaluation');
    } else if (this.activeTab === 'bookmarked') {
      filtered = this.allChallenges.filter(c => c.badge === 'Bookmarked');
    }

    this.challenges = filtered;
    this.updatePagination();
  }

  updatePagination(): void {
    this.totalPages = Math.ceil(this.challenges.length / this.itemsPerPage);
    if (this.currentPage > this.totalPages) {
      this.currentPage = 1;
    }
  }

  onPageChange(page: number): void {
    if (this.activeTab === 'bookmarked') {
      this.loadBookmarkedChallenges(page);
    } else if (this.activeTab === 'ongoing-challenges') {
      this.loadUnderEvaluationChallenges(page);
    } else if (this.activeTab === 'past-challenges') {
      this.loadPastChallenges(page);
    } else {
      this.currentPage = page;
    }
  }

  onViewModeChange(mode: ViewMode): void {
    this.viewMode = mode;
  }

  toggleBookmarksTabShowDropdown(): void {
    this.bookmarksTabShowDropdownOpen = !this.bookmarksTabShowDropdownOpen;
  }

  onBookmarksTabItemsPerPageChange(value: number): void {
    this.itemsPerPage = value;
    this.bookmarksTabShowDropdownOpen = false;
    this.loadBookmarkedChallenges(1);
  }

  /** Card data for Past Challenges or Under Evaluation tab (same design as New Challenges). */
  get pastOrUnderEvaluationCardData(): ChallengeCardData[] {
    return this.activeTab === 'past-challenges' ? this.pastChallengesCardData : this.underEvaluationCardData;
  }

  togglePastUnderEvalShowDropdown(): void {
    this.pastUnderEvalShowDropdownOpen = !this.pastUnderEvalShowDropdownOpen;
  }

  onPastUnderEvalItemsPerPageChange(value: number): void {
    this.itemsPerPage = value;
    this.pastUnderEvalShowDropdownOpen = false;
    if (this.activeTab === 'past-challenges') {
      this.loadPastChallenges(1);
    } else if (this.activeTab === 'ongoing-challenges') {
      this.loadUnderEvaluationChallenges(1);
    }
  }

  /** Page numbers for Past / Under Evaluation pagination (max 5 visible). */
  getPastUnderEvalPageNumbers(): number[] {
    const pages: number[] = [];
    const maxVisible = 5;
    const total = this.totalPages;
    let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(total, start + maxVisible - 1);
    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }

  /** Page numbers for Bookmarks tab pagination (max 5 visible). */
  getBookmarksTabPageNumbers(): number[] {
    const pages: number[] = [];
    const maxVisible = 5;
    const total = this.totalPages;
    let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(total, start + maxVisible - 1);
    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }

  onSearchChange(searchTerm: string): void {
    // TODO: Implement search filtering
    console.log('Search:', searchTerm);
  }

  onItemsPerPageChange(items: number): void {
    this.itemsPerPage = items;
    this.currentPage = 1;
    if (this.activeTab === 'bookmarked') {
      this.loadBookmarkedChallenges(1);
    } else if (this.activeTab === 'ongoing-challenges') {
      this.loadUnderEvaluationChallenges(1);
    } else if (this.activeTab === 'past-challenges') {
      this.loadPastChallenges(1);
    } else {
      this.updatePagination();
    }
  }

  onNewChallengesSlideChange(event: any): void {
    // Handle carousel slide change
  }

  onUnderEvaluationSlideChange(event: any): void {
    // Handle carousel slide change
  }

  onPastChallengesSlideChange(event: any): void {
    // Handle carousel slide change
  }

  navigateToDetail(id: string): void {
    this.googleAnalytics.trackChallengeClick('', id);
    this.router.navigate(['/challenges', id]);
  }
}
